import last from 'lodash/last';
import * as React from 'react';
import type { NavigationAnchor } from './types';

export function useCurrentlyVisibleSection(sectionAnchors: NavigationAnchor[]) {
    const [currentVisibleSectionId, setCurrentVisibleSectionId] = React.useState<string | null>(null);

    React.useEffect(() => {
        const nodes = sectionAnchors.map(anchor => {
            const { id } = anchor;
            const element = document.getElementById(id);

            return { element, id };
        });

        const visibilityMap = new Map<string, boolean>();

        function manageVisibility(entries: IntersectionObserverEntry[]) {
            for (const entry of entries) {
                visibilityMap.set(entry.target.id, entry.isIntersecting);
            }
        }

        function manageFirstVisibleElement() {
            const [visibleElementId] = Array.from(visibilityMap.entries())
                .filter(([, value]) => value)
                .map(([key]) => key);

            setCurrentVisibleSectionId(visibleElementId ?? last(nodes)?.id);
        }

        const observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
                manageVisibility(entries);
                manageFirstVisibleElement();
            },
            {
                rootMargin: '-128px 0px 0px 0px',
                threshold: [0, 1],
            }
        );

        nodes.forEach(node => {
            if (node.element) {
                visibilityMap.set(node.id, false);
                observer.observe(node.element);
            }
        });

        return () => observer.disconnect();
    }, [sectionAnchors]);

    return { currentVisibleSectionId };
}
